import {
  Job, ApiResponse, JobShift, CreateUserData, LaborConditionsTemplate, LaborConditionsAgreementSummary, User, StoreUser,
  StoreDetail, SystemSetting, FAQ, BankAccount, BankAccountResponse, BankAccountRegisterResponse,
  MyApplicationDetail, TodayShiftsResult,
  WalletBalance, WalletTransactionsResponse, WithdrawalSimulation, WithdrawalExecutionResult,
  WithdrawalHistoryItem,
  RadarObject, SkillObject, GlobalRankPublicObject, StoreRankPublicObject, JobPredictionObject,
  SpecialOffer, SpecialOfferRespondStatus, UnseenSkillsResponse, MarkSeenResponse,
  UnacquiredSkillsResponse,
  TermsConsentStatus, TermsConsentPayload,
} from '../types';
import LiffService from './liff';
import { isGamificationMockEnabled, gamificationMock } from '../mocks/gamification.mock';

// 環境変数からAPI URLを取得（Docker/本番環境対応）
// REACT_APP_API_URL: docker-compose.dev.ymlで"/api"に設定
// ローカル開発時はsetupProxy.jsでプロキシ経由でバックエンドに接続
const API_URL = process.env.REACT_APP_API_URL || '/api';

// サーバから返る文字列に内部情報（SQL・スタックトレース・DBドライバ名等）が
// 含まれていないか判定する。バックエンド側（app/core/error_handling.py）でも
// 同じ判定をしているが、古いバックエンドや将来の別APIから漏れた場合の保険として
// クライアント側にも同じガードを置く（設計書 §4-5 の層3）。
const SENSITIVE_SERVER_TEXT_PATTERNS = [
  '[sql:',
  '[parameters:',
  'select ',
  'insert into',
  'delete from',
  ' from ',
  ' where ',
  'sqlalchemy',
  'pg8000',
  'psycopg',
  'does not exist',
  'relation "',
  'column "',
  'constraint "',
  'duplicate key value',
  'traceback (most recent call last)',
  'file "',
  '.py", line',
  'site-packages',
  'alembic',
  'postgresql://',
  'integrityerror',
  'programmingerror',
  'operationalerror',
];

export const containsSensitiveServerText = (text: unknown): boolean => {
  if (typeof text !== 'string' || !text) return false;
  const lowered = text.toLowerCase();
  return SENSITIVE_SERVER_TEXT_PATTERNS.some((pattern) => lowered.includes(pattern));
};

const GENERIC_SERVER_ERROR_MESSAGE =
  'サーバーで問題が発生しました。時間をおいて再度お試しください。';

class ApiService {
  private formatFastApiValidationError(errorItem: any): string {
    if (!errorItem || typeof errorItem !== 'object') return '';

    const rawMessage = typeof errorItem.msg === 'string' ? errorItem.msg : '';
    const message = rawMessage.replace(/^Value error,\s*/i, '').trim();

    if (message) {
      return message;
    }

    return '';
  }

  /**
   * サーバ由来のエラー値を画面表示用の文字列に正規化する。
   *
   * `status` を渡した場合、5xx ではサーバ文字列を一切採用せず汎用文言にする。
   * 5xx はユーザーが対処できないため文言に情報価値が無く、内部情報の漏洩経路に
   * なるだけだという判断（設計書 §4-5）。
   * 4xx でも内部情報を含む文字列は汎用文言に差し替える。
   */
  private normalizeErrorMessage(errorValue: any, status?: number): string {
    if (typeof status === 'number' && status >= 500) {
      return GENERIC_SERVER_ERROR_MESSAGE;
    }
    const message = this.extractErrorMessage(errorValue);
    // 抽出後の文字列も判定する（detail / error / message のどの経路で来ても塞ぐ）
    return containsSensitiveServerText(message) ? GENERIC_SERVER_ERROR_MESSAGE : message;
  }

  private extractErrorMessage(errorValue: any): string {
    if (!errorValue) return 'An error occurred';
    if (typeof errorValue === 'string') return errorValue;
    if (typeof errorValue === 'number' || typeof errorValue === 'boolean') return String(errorValue);
    if (Array.isArray(errorValue)) {
      const messages = errorValue
        .map((item) => this.formatFastApiValidationError(item))
        .filter((message) => !!message);

      if (messages.length > 0) {
        return messages.join(' / ');
      }

      try {
        return JSON.stringify(errorValue);
      } catch {
        return 'An error occurred';
      }
    }

    if (typeof errorValue === 'object') {
      if (typeof errorValue.message === 'string' && errorValue.message.trim()) {
        return errorValue.message;
      }
      if (typeof errorValue.detail === 'string' && errorValue.detail.trim()) {
        return errorValue.detail;
      }
      if (Array.isArray(errorValue.detail)) {
        return this.extractErrorMessage(errorValue.detail);
      }
      if (typeof errorValue.error === 'string' && errorValue.error.trim()) {
        return errorValue.error;
      }
      try {
        return JSON.stringify(errorValue);
      } catch {
        return 'An error occurred';
      }
    }

    return 'An error occurred';
  }

  // 一過性の接続エラー（DNS/コネクション断/タイムアウト等）かどうかの判定。
  // これらはリトライで自己回復しうるため、業務エラー（未登録・バリデーション等）と区別する。
  isRetryableError(message?: string | null): boolean {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return [
      'all connection attempts failed',
      'failed to fetch',
      'networkerror',
      'load failed', // Safari/WebKitのネットワークエラー文言
      'timed out',
      'timeout',
      'aborted',
      'econnrefused',
      'econnreset',
      'etimedout',
      'enotfound',
      'connection',
    ].some((needle) => normalized.includes(needle));
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {},
    isBlobResponse: boolean = false,
    timeoutMs?: number,
    // 既定では `{success, data}` 形式の data だけを取り出すが、data の外側にある
    // メタ情報（today-shifts の registered など）が必要な場合は true でボディ全体を返す。
    keepEnvelope: boolean = false
  ): Promise<ApiResponse<T>> {
    const controller = timeoutMs ? new AbortController() : undefined;
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const accessToken = await LiffService.getAccessToken();

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options.headers,
      };

      if (accessToken) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        signal: controller?.signal,
      });
      
      // Blobレスポンスの場合は特別な処理
      if (isBlobResponse) {
        if (!response.ok) {
          const errorText = await response.text();
          return {
            success: false,
            error: errorText || 'Failed to fetch blob data',
          };
        }
        const blob = await response.blob();
        return {
          success: true,
          data: blob as any,
        };
      }
      
      // 204 No Content や空ボディのレスポンスはJSONパースできないため先に処理する
      const responseText = await response.text();
      if (!responseText) {
        if (!response.ok) {
          return {
            success: false,
            error: `リクエストに失敗しました (HTTP ${response.status})`,
          };
        }
        return { success: true };
      }

      let data: any;
      try {
        data = JSON.parse(responseText);
      } catch {
        return {
          success: false,
          error: response.ok
            ? 'レスポンスの解析に失敗しました'
            : `リクエストに失敗しました (HTTP ${response.status})`,
        };
      }

      // ボディが "null" の場合（204にボディが付くケース等）は空ボディと同様に扱う
      // （後続の data.data 参照で TypeError になり、成功なのに失敗扱いされるのを防ぐ）
      if (data === null || data === undefined) {
        if (!response.ok) {
          return {
            success: false,
            error: `リクエストに失敗しました (HTTP ${response.status})`,
          };
        }
        return { success: true };
      }

      // HTTPステータスが200でも、API本文で success=false の場合は失敗として扱う
      if (data && typeof data === 'object' && 'success' in data && data.success === false) {
        // HTTP 200 で success=false の経路（QR出退勤等）。status は 200 なので
        // 5xx ガードは効かないが、内容ベースの判定で内部情報を塞ぐ。
        return {
          success: false,
          error: this.normalizeErrorMessage(data.error || data.detail || data.message),
          data: data.data || data,
        };
      }

      if (!response.ok) {
        return {
          success: false,
          error: this.normalizeErrorMessage(
            data.error || data.detail || data.message,
            response.status
          ),
        };
      }
      
      return {
        success: true,
        data: keepEnvelope ? data : (data.data || data), // FastAPIはdataプロパティで応答を返す場合がある
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // Job endpoints
  async getJob(id: string): Promise<ApiResponse<Job>> {
    return this.fetchWithAuth<Job>(`/templates/job-posting/${id}`);
  }

  // 最新の労働条件通知書テンプレートを取得
  async getLatestLaborConditionsTemplate(): Promise<ApiResponse<LaborConditionsTemplate[]>> {
    return this.fetchWithAuth<LaborConditionsTemplate[]>('/templates/labor-conditions?limit=1&is_active=true');
  }

  // 利用可能な職種を取得
  async getAvailableJob(): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/job-positions/`);
  }
 
  // 利用可能な求人シフトを取得
  async getAvailableShifts(userId: string | null, date?: string, days: number = 30): Promise<ApiResponse<{shifts: Record<string, JobShift[]>}>> {
    let query = `/line-mini/available-shifts?user_id=${userId}&days=${days}`;
    if (date) {
      query += `&from_date=${date}`
    }
    return this.fetchWithAuth<{shifts: Record<string, JobShift[]>}>(query);
  }

  // 特定のシフト詳細を取得
  async getShiftDetail(shiftId: string, userId?: string | null): Promise<ApiResponse<JobShift>> {
    const query = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return this.fetchWithAuth<JobShift>(`/line-mini/shift/${shiftId}${query}`);
  }

  // シフトへの応募
  async applyForShift(shiftId: string, userData?: {
    userId?: string;
    last_name?: string;
    first_name?: string;
    email?: string;
    phone?: string;
    agreedToContract?: boolean;
    agreedToLaborConditions?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/line-mini/apply-shift/${shiftId}`, {
      method: 'POST',
      body: JSON.stringify(userData || {}),
    });
  }

  // 自分の応募履歴（店舗・シフト情報付き）を取得
  // fromDate 未指定時はバックエンドが今日（JST）以降にフィルタする
  async getMyApplicationsWithDetails(
    userId: string | null,
    fromDate?: string
  ): Promise<ApiResponse<MyApplicationDetail[]>> {
    if (!userId) {
      return { success: false, error: 'ユーザーIDがありません' };
    }
    const params = new URLSearchParams({ user_id: userId });
    if (fromDate) params.set('from_date', fromDate);
    return this.fetchWithAuth<MyApplicationDetail[]>(`/applications/with-details?${params.toString()}`);
  }

  // 労働条件通知書のPDFを取得（画像データとして）
  async getLaborConditionsPDF(jobId: string, shiftId: string, userId: string | null): Promise<ApiResponse<{
    image_data: Array<{page: number, data: string}>,
    total_pages: number,
    filename: string,
    pdf_url?: string  // 従来の互換性のため
  }>> {
    return this.fetchWithAuth<{
      image_data: Array<{page: number, data: string}>,
      total_pages: number,
      filename: string,
      pdf_url?: string
    }>(
      `/templates/labor-conditions/generate-pdf?job_id=${jobId}&shift_id=${shiftId}&user_id=${userId || ''}`
    );
  }

  // 同意済み労働条件通知書の一覧を取得
  async getLaborConditionsAgreements(userId: string): Promise<ApiResponse<LaborConditionsAgreementSummary[]>> {
    return this.fetchWithAuth<LaborConditionsAgreementSummary[]>(
      `/templates/labor-conditions/agreements?user_id=${userId}`
    );
  }

  // 同意済み労働条件通知書PDFのダウンロードURLを生成（外部ブラウザで開く用の絶対URL）
  getLaborConditionsDownloadUrl(agreementId: string, userId: string): string {
    const base = /^https?:\/\//.test(API_URL) ? API_URL : `${window.location.origin}${API_URL}`;
    return `${base}/templates/labor-conditions/agreements/${agreementId}/download?user_id=${encodeURIComponent(userId)}`;
  }

  // 労働条件通知書への同意を保存
  async saveLaborConditionsAgreement(
    jobId: string, 
    shiftId: string, 
    applicationId: string,
    userData: any
  ): Promise<ApiResponse<any>> {
    const queryParams = `?job_id=${jobId}&shift_id=${shiftId}&application_id=${applicationId}&agreed=true`;
    
    return this.fetchWithAuth<any>(`/templates/labor-conditions/save-agreement${queryParams}`, {
      method: 'POST',
      body: JSON.stringify(userData || {}),
    });
  }

  // 登録店舗の情報を取得
  async getStoreUser(user_id: string | null): Promise<ApiResponse<StoreUser[]>> {
    return this.fetchWithAuth<StoreUser[]>(`/store-users/user_id/${user_id}/?is_active=true&is_blocked=false`);
  }
  // 店舗詳細情報を取得（住所・電話番号など）
  async getStore(store_id: string): Promise<ApiResponse<StoreDetail>> {
    return this.fetchWithAuth<StoreDetail>(`/store/?store_id=${store_id}`);
  }
  // 登録店舗へのメモを更新
  async updateStoreMemo(storeUserId: string, memo: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/store-users/${storeUserId}`, {
      method: 'PUT',
      body: JSON.stringify({ memo }),
    });
  }
  // 登録店舗の情報を削除
  // 論理削除のみ（物理削除の場合はidの後ろに/permanentつける）
  async deleteBookmark(id: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `/store-users/${id}`, {
        method: 'DELETE'
      });
  }

  // ユーザー情報の削除 Re:crew退会で使用
  // 論理削除のみ（物理削除の場合はidの後ろに/permanentつける）
  async deleteUsers(id: string | null): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(
      `/users/${id}`, {
        method: 'DELETE'
      });
  }
  // User情報を取得
  async getUser(userId: string | null): Promise<ApiResponse<User>> {
    return this.fetchWithAuth<User>(`/users/${userId}`);
  }

  // User情報を更新
  async updateUser(userId: string | null, userData: Partial<User>): Promise<ApiResponse<User>> {
    return this.fetchWithAuth<User>(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // アバター画像をアップロード（multipart/form-data）
  async uploadAvatar(userId: string, file: File): Promise<ApiResponse<User>> {
    try {
      const accessToken = await LiffService.getAccessToken();
      const formData = new FormData();
      formData.append('avatar', file);
      const headers: Record<string, string> = {};
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
      const response = await fetch(`${API_URL}/users/${userId}/avatar`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!response.ok) {
        // プロキシ等がHTMLで応答するケース（413など）はJSONパースできない
        const data = await response.json().catch(() => null);
        const errorMessage =
          typeof data?.detail === 'string'
            ? data.detail
            : response.status === 413
              ? '画像サイズが大きすぎるためアップロードできませんでした。'
              : `アバターのアップロードに失敗しました（エラーコード: ${response.status}）`;
        return { success: false, error: errorMessage };
      }
      const data = await response.json();
      // バックエンドは User オブジェクトを直接返す
      return { success: true, data: data as User };
    } catch (err) {
      return { success: false, error: 'アバターのアップロードに失敗しました' };
    }
  }

  // ユーザの存在確認
  // LINEトークン検証のためバックエンドが外部通信するチェックで、一過性の接続失敗が起きやすい。
  // 読み取り専用のチェックで冪等なため、接続系エラーに限り数回リトライして自己回復させる。
  async checkUserExist(accessToken: string, storeId?: string): Promise<ApiResponse<any>> {
    const body: any = { access_token: accessToken };
    if (storeId) {
      body.store_id = storeId;
    }
    const retryDelaysMs = [400, 1000];
    let result: ApiResponse<any>;
    for (let attempt = 0; ; attempt++) {
      result = await this.fetchWithAuth<any>(
        '/applications/check-user-exist',
        { method: 'POST', body: JSON.stringify(body) },
        false,
        12000
      );
      if (result.success || !this.isRetryableError(result.error) || attempt >= retryDelaysMs.length) {
        return result;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
    }
  }

  // 退会したアカウントの利用を再開する（退会済みダイアログの「利用を再開する」）
  // users.is_active を戻し、退会で無効化された店舗の所属もまとめて復活させる。
  async reactivateAccount(userId: string, storeId: string, lineUserId: string | null): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/users/${userId}/reactivate`, {
      method: 'POST',
      body: JSON.stringify({ store_id: storeId, line_user_id: lineUserId }),
    });
  }

  // 既存ユーザーを店舗に追加登録
  async addStoreRegistration(userId: string, storeId: string, lineUserId: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      user_id: userId,
      store_id: storeId,
      line_user_id: lineUserId,
    });
    return this.fetchWithAuth<any>(`/store-users/by-relation?${params.toString()}`, {
      method: 'POST',
    });
  }

  // 店舗に紐づいた新規ユーザー作成
  async createUser(registrationData: CreateUserData): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>('/users/with-store', {
      method: 'POST',
      body: JSON.stringify(registrationData),
    });
  }

  // line-front側のシステム設定取得
  async getSystemSetting(): Promise<ApiResponse<SystemSetting[]>> {
    return this.fetchWithAuth<any>(`/system-settings/line-front/settings`);
  }
  // 店舗IDからLIFF IDを取得
async getLiffIdByStoreId(storeId: string): Promise<ApiResponse<any>> {
  try {
    const response = await fetch(`${API_URL}/store/${storeId}/liff-id`);
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        error: this.normalizeErrorMessage(
          data.detail || data.error || data.message || `Store LIFF ID取得失敗 (HTTP ${response.status})`,
          response.status
        ),
      };
    }
    
    return {
      success: true,
      data: data.data || data,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

  // LIFF IDから店舗IDを取得（逆引き）
  async getStoreIdByLiffId(liffId: string): Promise<ApiResponse<{ store_id: string; liff_id: string }>> {
    try {
      const response = await fetch(`${API_URL}/store/by-liff/${liffId}/store-id`);
      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: this.normalizeErrorMessage(
            data.detail || 'LIFF IDから店舗情報の取得に失敗しました', response.status
          ),
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }

  // シフトIDから店舗IDとLIFF IDを取得
  async getStoreInfoByShiftId(shiftId: string): Promise<ApiResponse<{ store_id: string; liff_id: string }>> {
    try {
      const response = await fetch(`${API_URL}/store/by-shift/${shiftId}/liff-info`);
      const data = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: this.normalizeErrorMessage(
            data.detail || 'シフトから店舗情報の取得に失敗しました', response.status
          ),
        };
      }
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }

  // FAQ一覧を取得
  async getFAQs(): Promise<ApiResponse<FAQ[]>> {
    return this.fetchWithAuth<FAQ[]>('/system-settings/faqs');
  }

  // 特定のFAQを取得
  async getFAQ(id: string): Promise<ApiResponse<FAQ>> {
    return this.fetchWithAuth<FAQ>(`/system-settings/faqs${id}`);
  }

  // ===== Attendance API Endpoints =====
  // 現在の勤務状況を取得
  async getAttendanceCurrent(storeUserId: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/attendance/current/${storeUserId}`);
  }

  // 出勤打刻
  async attendanceCheckIn(storeUserId: string, request: {
    qr_payload: string;
    device_info?: string;
  }): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/attendance/check-in?store_user_id=${storeUserId}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // 退勤打刻
  async attendanceCheckOut(storeUserId: string, request: {
    qr_payload: string;
    device_info?: string;
  }): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/attendance/check-out?store_user_id=${storeUserId}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // 勤怠履歴を取得
  async getAttendanceHistory(storeUserId: string, limit: number = 50, offset: number = 0): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/attendance/history/${storeUserId}?limit=${limit}&offset=${offset}`);
  }

  // 本日の勤務予定＋「この店舗にクルー登録があるか」まで取得する。
  // 他店舗のQR（QR自体は有効だが自分の勤務先ではない）を「予定なし」と誤表示しないために使う。
  async getTodayShiftsWithStatus(
    storeId: string,
    userId: string,
  ): Promise<ApiResponse<TodayShiftsResult>> {
    return this.fetchWithAuth<TodayShiftsResult>(
      `/attendance/today-shifts?store_id=${encodeURIComponent(storeId)}&user_id=${encodeURIComponent(userId)}`,
      {},
      false,
      undefined,
      true,
    );
  }

  // ===== Wallet API Endpoints（複数企業対応） =====
  // 残高取得（user_id ベース、合算 + 企業別内訳、即給残高同期付き）
  async getWalletBalance(
    userId: string,
    options: { syncSokyu?: boolean } = {},
  ): Promise<ApiResponse<WalletBalance>> {
    const params = new URLSearchParams();
    if (options.syncSokyu === false) params.set('sync_sokyu', 'false');
    const qs = params.toString();
    return this.fetchWithAuth<WalletBalance>(
      `/users/${userId}/wallet/balance${qs ? `?${qs}` : ''}`,
    );
  }

  // 取引履歴（user_id ベース、全企業横断 or company_id フィルタ）
  async getWalletTransactions(
    userId: string,
    limit: number = 50,
    offset: number = 0,
    options: { companyId?: string; transactionType?: string; dateFrom?: string; dateTo?: string } = {},
  ): Promise<ApiResponse<WalletTransactionsResponse>> {
    const params = new URLSearchParams({ limit: String(limit), skip: String(offset) });
    if (options.companyId) params.set('company_id', options.companyId);
    if (options.transactionType) params.set('transaction_type', options.transactionType);
    if (options.dateFrom) params.set('date_from', options.dateFrom);
    if (options.dateTo) params.set('date_to', options.dateTo);
    return this.fetchWithAuth<WalletTransactionsResponse>(
      `/users/${userId}/wallet/transactions?${params.toString()}`,
    );
  }

  // 統計情報（user_id ベース）
  async getWalletStatistics(userId: string, days: number = 7, companyId?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ days: String(days) });
    if (companyId) params.set('company_id', companyId);
    return this.fetchWithAuth<any>(`/users/${userId}/wallet/stats?${params.toString()}`);
  }

  // 全額引き出しのシミュレーション（DB 書き込みなし）
  async simulateWithdrawal(
    userId: string,
  ): Promise<ApiResponse<WithdrawalSimulation>> {
    return this.fetchWithAuth<WithdrawalSimulation>(
      `/users/${userId}/wallet/withdrawal/simulate`,
      { method: 'POST' },
    );
  }

  // 全額引き出しの実行（即給API連携、親子レコード作成）
  async executeWithdrawal(
    userId: string,
  ): Promise<ApiResponse<WithdrawalExecutionResult>> {
    return this.fetchWithAuth<WithdrawalExecutionResult>(
      `/users/${userId}/wallet/withdrawal/execute`,
      { method: 'POST' },
    );
  }

  // 引き出し履歴一覧（store_user_id ベースの互換エンドポイント）
  async getWalletWithdrawalRequests(
    storeUserId: string,
    limit: number = 20,
    offset: number = 0,
    yearMonth?: string,
  ): Promise<ApiResponse<{ requests: WithdrawalHistoryItem[]; total_count: number }>> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (yearMonth) params.set('year_month', yearMonth);
    return this.fetchWithAuth(
      `/wallet/withdrawal-requests/${storeUserId}?${params.toString()}`,
    );
  }

  // ハッシュチェーンを検証（user_id ベース、全ウォレット個別検証）
  async verifyWalletChain(userId: string, companyId?: string): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    const qs = params.toString();
    return this.fetchWithAuth<any>(
      `/users/${userId}/wallet/verify-chain${qs ? `?${qs}` : ''}`,
    );
  }

  // ===== Bank Account API Endpoints =====
  // 口座情報を取得
  async getBankAccount(userId: string): Promise<ApiResponse<BankAccountResponse>> {
    return this.fetchWithAuth<BankAccountResponse>(`/users/${userId}/bank-account`);
  }

  // 口座情報を登録（即給API連携含む）
  async registerBankAccount(userId: string, bankAccount: BankAccount): Promise<ApiResponse<BankAccountRegisterResponse>> {
    return this.fetchWithAuth<BankAccountRegisterResponse>(`/users/${userId}/bank-account`, {
      method: 'PUT',
      body: JSON.stringify(bankAccount),
    });
  }

  // 利用規約の同意状況を取得（課題88・口座登録ゲート判定用）
  async getTermsConsentStatus(userId: string): Promise<ApiResponse<TermsConsentStatus>> {
    return this.fetchWithAuth<TermsConsentStatus>(`/users/${userId}/terms-consents/status`);
  }

  // 利用規約同意を記録（課題88）
  async recordTermsConsent(userId: string, payload: TermsConsentPayload): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/users/${userId}/terms-consents`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ===== ゲーミフィケーション API（ワーカー / line_access_token, 設計書 §5.3・付録D） =====
  // バックエンドのワーカー endpoint は LINE UID を **query param `line_access_token`** から
  // 解決する（applications/router.py get_my_applications 等と同じ規約）。fetchWithAuth が付与する
  // Authorization ヘッダは読まれないため、各メソッドで line_access_token を URL に明示付与する。
  // モックゲート ON 時は mocks/gamification.mock.ts のサンプルデータを返す（本番無影響）。

  // line_access_token を含むクエリ文字列を組み立てる（既存追加パラメータとマージ）。
  private async buildWorkerQuery(extra?: Record<string, string | number | undefined>): Promise<string> {
    const token = await LiffService.getAccessToken();
    const params = new URLSearchParams();
    if (token) params.set('line_access_token', token);
    if (extra) {
      Object.entries(extra).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.set(key, String(value));
      });
    }
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  // 獲得スキル + Lv（GET /api/store-users/{store_user_id}/skills）
  async getWorkerSkills(storeUserId: string): Promise<ApiResponse<{ skills: SkillObject[]; level: number }>> {
    if (isGamificationMockEnabled()) return gamificationMock.getSkills(storeUserId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<{ skills: SkillObject[]; level: number }>(`/store-users/${storeUserId}/skills${qs}`);
  }

  // 未習得スキル + マニュアルURL（GET /api/store-users/{store_user_id}/unacquired-skills）
  // 店舗の有効スキル定義のうち本人が未獲得のものを全件返す（manual_url は任意）。
  // モックは未対応のため本物 API のみ。
  async getUnacquiredSkills(storeUserId: string): Promise<ApiResponse<UnacquiredSkillsResponse>> {
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<UnacquiredSkillsResponse>(`/store-users/${storeUserId}/unacquired-skills${qs}`);
  }

  // レーダー5軸 + Lv（GET /api/store-users/{store_user_id}/skill-parameters）
  async getWorkerSkillParameters(storeUserId: string): Promise<ApiResponse<RadarObject>> {
    if (isGamificationMockEnabled()) return gamificationMock.getSkillParameters(storeUserId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<RadarObject>(`/store-users/${storeUserId}/skill-parameters${qs}`);
  }

  // 「この店舗での評価」= 店舗内ランク 公開マスク版
  // （GET /api/store-users/{store_user_id}/rank。管理画面クルー管理と同じ店舗ランクを参照）
  async getStoreUserRank(storeUserId: string): Promise<ApiResponse<StoreRankPublicObject>> {
    if (isGamificationMockEnabled()) return gamificationMock.getStoreUserRank(storeUserId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<StoreRankPublicObject>(`/store-users/${storeUserId}/rank${qs}`);
  }

  // 「すべての店舗での評価」= 横断ランク 公開マスク版
  // （GET /api/user/{user_id}/global-rank。global_score 等はサーバ側でマスク済み）
  async getGlobalRank(userId: string): Promise<ApiResponse<GlobalRankPublicObject>> {
    if (isGamificationMockEnabled()) return gamificationMock.getGlobalRank(userId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<GlobalRankPublicObject>(`/user/${userId}/global-rank${qs}`);
  }

  // 全店舗集計レーダー5軸 + Lv（GET /api/user/{user_id}/global-skill-parameters）
  // ※ バックエンド実装待ち。フロントは呼び出し側で404/失敗時に「集計データ準備中」を表示。
  async getGlobalSkillParameters(userId: string): Promise<ApiResponse<RadarObject>> {
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<RadarObject>(`/user/${userId}/global-skill-parameters${qs}`);
  }

  // 全店舗集計 獲得スキル（GET /api/user/{user_id}/global-skills）
  // ※ バックエンド実装待ち。フロントは呼び出し側で404/失敗時に「集計データ準備中」を表示。
  async getGlobalSkills(userId: string): Promise<ApiResponse<{ skills: SkillObject[]; level: number }>> {
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<{ skills: SkillObject[]; level: number }>(`/user/${userId}/global-skills${qs}`);
  }

  // ENTRY SENT 予測報酬 + 予測獲得スキル（GET /api/line-mini/jobs/{shift_id}/prediction）
  // バックエンドは store_user_id（本人。§7 自己所有・適格ティア算出）と line_access_token を
  // **必須 query param** で要求する。両者を付与する。
  async getJobPrediction(shiftId: string, storeUserId: string): Promise<ApiResponse<JobPredictionObject>> {
    if (isGamificationMockEnabled()) return gamificationMock.getJobPrediction(shiftId, storeUserId);
    const qs = await this.buildWorkerQuery({ store_user_id: storeUserId });
    return this.fetchWithAuth<JobPredictionObject>(`/line-mini/jobs/${shiftId}/prediction${qs}`);
  }

  // ===== ゲーミフィケーション Phase 5（特別オファー / 招待状 + GET SKILL） =====
  // すべて line_access_token 自己所有経路（buildWorkerQuery）で transport する。

  // 受信オファー / 招待状 一覧（GET /api/store-users/{store_user_id}/special-offers）
  async getSpecialOffers(storeUserId: string): Promise<ApiResponse<SpecialOffer[]>> {
    if (isGamificationMockEnabled()) return gamificationMock.getSpecialOffers(storeUserId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<SpecialOffer[]>(`/store-users/${storeUserId}/special-offers${qs}`);
  }

  // オファー / 招待状 の諾否（PATCH /api/store-users/{store_user_id}/special-offers/{offer_id}）
  async respondSpecialOffer(
    storeUserId: string,
    offerId: string,
    status: SpecialOfferRespondStatus,
  ): Promise<ApiResponse<SpecialOffer>> {
    if (isGamificationMockEnabled()) return gamificationMock.respondSpecialOffer(storeUserId, offerId, status);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<SpecialOffer>(`/store-users/${storeUserId}/special-offers/${offerId}${qs}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  // 未読の獲得スキル（GET /api/store-users/{store_user_id}/skills/unseen）
  // GET SKILL お祝い演出の駆動データ。
  async getUnseenSkills(storeUserId: string): Promise<ApiResponse<UnseenSkillsResponse>> {
    if (isGamificationMockEnabled()) return gamificationMock.getUnseenSkills(storeUserId);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<UnseenSkillsResponse>(`/store-users/${storeUserId}/skills/unseen${qs}`);
  }

  // GET SKILL 既読化（PATCH /api/store-users/{store_user_id}/skills/mark-seen）
  // 演出を表示したら呼ぶ。冪等（再呼び出しは marked=0）なので「1回だけ表示」を満たす。
  // skillIds 未指定（空配列）なら本人の全未読を既読化する。
  async markSkillsSeen(storeUserId: string, skillIds: string[] = []): Promise<ApiResponse<MarkSeenResponse>> {
    if (isGamificationMockEnabled()) return gamificationMock.markSkillsSeen(storeUserId, skillIds);
    const qs = await this.buildWorkerQuery();
    return this.fetchWithAuth<MarkSeenResponse>(`/store-users/${storeUserId}/skills/mark-seen${qs}`, {
      method: 'PATCH',
      body: JSON.stringify({ skill_ids: skillIds }),
    });
  }

  // ===== Health Check =====
  // Backend API への疎通確認
  async checkBackendHealth(): Promise<{ success: boolean; message: string; statusCode?: number }> {
    try {
      const response = await fetch(`${API_URL}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        return {
          success: true,
          message: `Backend API is accessible at ${API_URL}`,
          statusCode: response.status,
        };
      } else {
        return {
          success: false,
          message: `Backend API responded with status ${response.status}`,
          statusCode: response.status,
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        success: false,
        message: `Backend API is not accessible: ${errorMessage}. Trying to connect to ${API_URL}`,
      };
    }
  }

  // ===== Generic methods =====
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.fetchWithAuth<T>(endpoint);
  }

  // Generic POST request with auth
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.fetchWithAuth<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

const apiService = new ApiService();
export default apiService;